import * as THREE from 'three';
import { vehicleColliderFor } from './physics.js';
import { clampWeaponAimDirection } from './weaponAim.js';

// La altura del cañón es cosmética: todos los autos disparan sobre la misma
// línea de combate para que una carrocería alta o baja no altere el balance.
export const PROJECTILE_COMBAT_HEIGHT = .42;
export const PROJECTILE_SAME_SURFACE_TOLERANCE = .38;
export const PROJECTILE_VISUAL_CONVERGENCE = 24;

const finite = (value, fallback) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

function targetOrientationFromState(targetState) {
  if (!targetState) return null;
  const quaternionValues = [
    targetState.qx,
    targetState.qy,
    targetState.qz,
    targetState.qw,
  ].map(Number);
  if (quaternionValues.every(Number.isFinite)) {
    return new THREE.Quaternion(...quaternionValues).normalize();
  }
  const pitch = Number(targetState.pitch);
  const roll = Number(targetState.roll);
  if (!Number.isFinite(pitch) && !Number.isFinite(roll)) return null;
  return new THREE.Quaternion().setFromEuler(new THREE.Euler(
    Number.isFinite(pitch) ? pitch : 0,
    finite(targetState.yaw, 0),
    Number.isFinite(roll) ? roll : 0,
    'YXZ',
  ));
}

export function projectileLaunchState(vehicle, radius = 0, aimDirection = null) {
  const car = vehicle.car;
  const quaternion = car.getWorldQuaternion(new THREE.Quaternion());
  const forward = clampWeaponAimDirection(vehicle, aimDirection, new THREE.Vector3());
  const up = vehicle.grounded
    ? new THREE.Vector3(0, 1, 0)
    : new THREE.Vector3(0, 1, 0).applyQuaternion(quaternion).normalize();
  const data = car.userData || {};
  const length = Math.max(.15, finite(data.collisionLength, finite(data.length, 1)));
  const position = car.getWorldPosition(new THREE.Vector3())
    .addScaledVector(up, PROJECTILE_COMBAT_HEIGHT)
    .addScaledVector(forward, length * .5 + Math.max(0, radius) + .06);
  const launchSurfaceY = vehicle.grounded
    ? finite(vehicle.groundHeight, car.position.y)
    : car.position.y;
  return {
    position,
    forward,
    launchGrounded:Boolean(vehicle.grounded),
    launchSurfaceY,
  };
}

export function projectileInterceptTime(delta, inheritedVelocity, speed) {
  const safeSpeed=Math.max(.001,finite(speed,1));
  const a=inheritedVelocity.lengthSq()-safeSpeed*safeSpeed;
  const b=-2*delta.dot(inheritedVelocity);
  const c=delta.lengthSq();
  if(c<.000001)return 0;
  if(Math.abs(a)<.000001)return b<-.000001?Math.max(0,-c/b):Math.sqrt(c)/safeSpeed;
  const discriminant=b*b-4*a*c;
  if(discriminant<0)return Math.sqrt(c)/safeSpeed;
  const root=Math.sqrt(discriminant);
  const first=(-b-root)/(2*a);
  const second=(-b+root)/(2*a);
  const candidates=[first,second].filter((value)=>Number.isFinite(value)&&value>0);
  return candidates.length?Math.min(...candidates):Math.sqrt(c)/safeSpeed;
}

// Convierte un punto visible desde la cámara en una dirección física desde el
// cañón. También descuenta la velocidad heredada del auto para que derrapar o
// volar lateralmente no desplace el impacto respecto de la mira.
export function projectileDirectionToPoint(
  vehicle,
  point,
  speed,
  radius=0,
  target=new THREE.Vector3(),
) {
  const inheritedVelocity=(vehicle.velocity||new THREE.Vector3()).clone().multiplyScalar(.45);
  target.copy(point).sub(vehicle.car.getWorldPosition(new THREE.Vector3())).normalize();
  for(let iteration=0;iteration<3;iteration+=1){
    const launch=projectileLaunchState(vehicle,radius,target);
    const delta=point.clone().sub(launch.position);
    const time=projectileInterceptTime(delta,inheritedVelocity,speed);
    if(time<=.000001)break;
    target.copy(delta).addScaledVector(inheritedVelocity,-time).normalize();
    clampWeaponAimDirection(vehicle,target,target);
  }
  return target.normalize();
}

function clipAxis(start, delta, minimum, maximum, interval) {
  if (Math.abs(delta) < .000001) {
    return start >= minimum && start <= maximum;
  }
  const first = (minimum - start) / delta;
  const second = (maximum - start) / delta;
  interval.enter = Math.max(interval.enter, Math.min(first, second));
  interval.exit = Math.min(interval.exit, Math.max(first, second));
  return interval.enter <= interval.exit;
}

export function projectileObstacleIntersection(start,end,obstacle,radius=0) {
  const padding=Math.max(0,finite(radius,0));
  const delta=end.clone().sub(start);
  const interval={enter:0,exit:1};
  if(!clipAxis(
    start.y,
    delta.y,
    finite(obstacle.minY,-Infinity)-padding,
    finite(obstacle.maxY,Infinity)+padding,
    interval,
  ))return null;

  if(obstacle.shape==='circle'){
    const centerX=finite(obstacle.x,0);
    const centerZ=finite(obstacle.z,0);
    const expandedRadius=Math.max(0,finite(obstacle.radius,0)+padding);
    const offsetX=start.x-centerX;
    const offsetZ=start.z-centerZ;
    const a=delta.x*delta.x+delta.z*delta.z;
    const c=offsetX*offsetX+offsetZ*offsetZ-expandedRadius*expandedRadius;
    if(a<.000001){
      if(c>0)return null;
    }else{
      const b=2*(offsetX*delta.x+offsetZ*delta.z);
      const discriminant=b*b-4*a*c;
      if(discriminant<0)return null;
      const root=Math.sqrt(discriminant);
      interval.enter=Math.max(interval.enter,(-b-root)/(2*a));
      interval.exit=Math.min(interval.exit,(-b+root)/(2*a));
      if(interval.enter>interval.exit)return null;
    }
  }else{
    if(!clipAxis(start.x,delta.x,obstacle.minX-padding,obstacle.maxX+padding,interval))return null;
    if(!clipAxis(start.z,delta.z,obstacle.minZ-padding,obstacle.maxZ+padding,interval))return null;
  }
  if(interval.exit<0||interval.enter>1)return null;
  const time=THREE.MathUtils.clamp(interval.enter,0,1);
  return {time,position:start.clone().lerp(end,time),obstacle};
}

// Intersección barrida de un proyectil contra el collider calibrado del auto.
// En la misma superficie se usa la huella XZ del vehículo como asistencia
// arcade. En distintas alturas o durante el vuelo se conserva el volumen 3D.
export function projectileVehicleIntersection(
  start,
  end,
  vehicle,
  radius,
  {
    launchGrounded = false,
    launchSurfaceY = 0,
    targetState = null,
  } = {},
) {
  const targetPosition=targetState
    ?new THREE.Vector3(targetState.x,targetState.y,targetState.z)
    :vehicle.car.position;
  const targetYaw=targetState?finite(targetState.yaw,vehicle.yaw):null;
  const targetOrientation=targetOrientationFromState(targetState);
  const targetGrounded=targetState
    ?Boolean(targetState.grounded)
    :vehicle.grounded;
  // Con la rotación aérea libre el modelo puede quedar inclinado o de lado.
  // En tiempo real usamos su quaternion completo; al rebobinar online usamos
  // la orientación histórica en vez del antiguo collider siempre vertical.
  const collider = vehicleColliderFor(
    vehicle,
    targetPosition,
    targetYaw,
    targetOrientation,
  );
  const targetSurfaceY = targetGrounded
    ?finite(targetState?.groundHeight,finite(vehicle.groundHeight,targetPosition.y))
    :targetPosition.y;
  const sameSurface = Boolean(
    launchGrounded
    && targetGrounded
    && Math.abs(targetSurfaceY - launchSurfaceY) <= PROJECTILE_SAME_SURFACE_TOLERANCE
  );
  const startDeltaX = start.x - collider.centerX;
  const startDeltaZ = start.z - collider.centerZ;
  const endDeltaX = end.x - collider.centerX;
  const endDeltaZ = end.z - collider.centerZ;
  const localStartX = startDeltaX * collider.rightX + startDeltaZ * collider.rightZ;
  const localStartZ = startDeltaX * collider.forwardX + startDeltaZ * collider.forwardZ;
  const localEndX = endDeltaX * collider.rightX + endDeltaZ * collider.rightZ;
  const localEndZ = endDeltaX * collider.forwardX + endDeltaZ * collider.forwardZ;
  const interval = { enter:0, exit:1 };
  const padding = Math.max(0, finite(radius, 0));

  if (!clipAxis(
    localStartX,
    localEndX - localStartX,
    -collider.halfWidth - padding,
    collider.halfWidth + padding,
    interval,
  )) return null;
  if (!clipAxis(
    localStartZ,
    localEndZ - localStartZ,
    -collider.halfLength - padding,
    collider.halfLength + padding,
    interval,
  )) return null;
  if (!sameSurface && !clipAxis(
    start.y,
    end.y - start.y,
    collider.bottom - padding,
    collider.top + padding,
    interval,
  )) return null;
  if (interval.exit < 0 || interval.enter > 1) return null;

  const time = THREE.MathUtils.clamp(interval.enter, 0, 1);
  return {
    time,
    sameSurface,
    position:start.clone().lerp(end, time),
    collider,
  };
}

export function convergeProjectileVisualOffset(offset, dt) {
  offset.multiplyScalar(Math.exp(-PROJECTILE_VISUAL_CONVERGENCE * Math.max(0, dt)));
  if (offset.lengthSq() < .000001) offset.set(0, 0, 0);
  return offset;
}
